import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Upload } from 'lucide-react';
import ComponentSearchPanel from './ComponentSearchPanel';
import ModelBuilderTable from './ModelBuilderTable';
import { productService } from '../../../services';
import { formatPrice } from '../../../utils/formatPrice';
import '../../../css/models-common.css';
import '../../../css/model-builder.css';

/**
 * Full-screen transparent overlay for creating/editing product models
 * Supports sub-product grouping: select 2+ components, click Group to create sub-product
 */
const ModelBuilderOverlay = ({ isOpen, onClose, modelId = null, onSave, initialData = null }) => {
    const overlayRef = useRef(null);
    const imageInputRef = useRef(null);

    // Model data state
    const [modelName, setModelName] = useState('');
    const [components, setComponents] = useState([]);
    const [subProducts, setSubProducts] = useState([]); // Array of sub-product groups
    const [modelDescription, setModelDescription] = useState('');
    const [selectedCurrency, setSelectedCurrency] = useState('USD');
    const [selectedUnit, setSelectedUnit] = useState('Case');
    const [totalPrice, setTotalPrice] = useState(0);
    const [totalAmount, setTotalAmount] = useState(1);
    const [imageFile, setImageFile] = useState(null); // File object for upload
    const [imagePreview, setImagePreview] = useState(null); // Local preview URL

    // Pre-populate fields when opening with initialData (edit mode)
    useEffect(() => {
        if (isOpen && initialData) {
            setModelName(initialData.name || '');
            setModelDescription(initialData.description || '');
            setSelectedCurrency(initialData.currency || 'USD');
            setSelectedUnit(initialData.unit || 'Case');
            setTotalPrice(initialData.price || 0);
            setTotalAmount(initialData.amount || 1);
            setComponents(initialData.components || []);
            setSubProducts(initialData.subProducts || []);
            setImageFile(null);
            setImagePreview(initialData.imageUrl || null);
        } else if (isOpen && !initialData) {
            // Reset to defaults for create mode
            setModelName('');
            setModelDescription('');
            setSelectedCurrency('USD');
            setSelectedUnit('Case');
            setTotalPrice(0);
            setTotalAmount(1);
            setComponents([]);
            setSubProducts([]);
            setImageFile(null);
            setImagePreview(null);
        }
    }, [isOpen, initialData]);

    // Handle ESC key to close
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Clean up image preview URL on unmount
    useEffect(() => {
        return () => {
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [imagePreview]);

    // Handle image file selection
    const handleImageSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Validate file type
        if (!file.type.startsWith('image/')) return;
        setImageFile(file);
        // Revoke old preview URL before creating new one
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setImagePreview(URL.createObjectURL(file));
    };

    // Prevent body scroll when overlay is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    // Transform backend component to builder component shape
    const transformBackendComponent = useCallback((comp, index) => ({
        id: comp.id || Date.now() + index,
        name: comp.name,
        price: comp.price || 0,
        amount: comp.quantity || 1,
        currency: comp.currency || selectedCurrency,
        unit: comp.unit || 'kg',
        source: comp.source_country || '',
        destination: comp.destination_country || '',
        selected: false,
        component_type: comp.component_type,
        material_id: comp.material_id,
        product_ref_id: comp.product_ref_id,
    }), [selectedCurrency]);

    // Fetch product detail and return its components transformed for the builder
    const fetchProductComponents = useCallback(async (productId) => {
        try {
            const detail = await productService.getDetail(productId);
            return (detail?.components || []).map(transformBackendComponent);
        } catch (err) {
            console.error('Failed to fetch product components:', err);
            return [];
        }
    }, [transformBackendComponent]);

    // Add component from search panel
    const handleAddComponent = async (component) => {
        // If it's a pre-defined product, add it as a sub-product block (locked)
        if (component.type === 'product') {
            const subProductId = Date.now();
            // Add sub-product immediately with empty components (shows loading state)
            const newSubProduct = {
                id: subProductId,
                name: component.name,
                type: 'product',
                locked: true,
                components: [],
                subtotal: component.price,
                currency: component.currency,
                unit: component.unit,
                amount: 1,
                collapsed: true,
                productRefId: component.id,
            };
            setSubProducts(prev => [...prev, newSubProduct]);

            // Fetch actual components from backend
            const fetchedComponents = await fetchProductComponents(component.id);
            setSubProducts(prev =>
                prev.map(sp =>
                    sp.id === subProductId
                        ? { ...sp, components: fetchedComponents }
                        : sp
                )
            );
        } else {
            // If it's a material, add as individual component
            const newComponent = {
                id: Date.now(),
                ...component,
                amount: 1,
                currency: selectedCurrency,
                unit: 'kg',
                source: 'Supplier A',
                destination: 'Factory B',
                selected: false,
            };
            setComponents([...components, newComponent]);
        }
    };

    // Remove component
    const handleRemoveComponent = (componentId) => {
        setComponents(components.filter(c => c.id !== componentId));
    };

    // Update component
    const handleUpdateComponent = (componentId, updates) => {
        setComponents(components.map(c =>
            c.id === componentId ? { ...c, ...updates } : c
        ));
    };

    // Count selected components
    const selectedCount = components.filter(c => c.selected).length;

    // Group selected components into a sub-product
    const handleGroupSelected = () => {
        const selectedComponents = [];
        const selectedIndices = [];
        components.forEach((c, idx) => {
            if (c.selected) {
                selectedComponents.push(c);
                selectedIndices.push(idx);
            }
        });
        if (selectedComponents.length < 2) return;

        // Calculate subtotal for the sub-product
        const subtotal = selectedComponents.reduce((sum, c) => sum + (c.price * c.amount), 0);

        // Store original indices so ungroup can restore positions
        const newSubProduct = {
            id: Date.now(),
            name: `Sub-Product ${subProducts.length + 1}`,
            components: selectedComponents.map((c, i) => ({
                ...c,
                selected: false,
                _originalIndex: selectedIndices[i],
            })),
            subtotal,
            currency: selectedCurrency,
            unit: selectedUnit,
            amount: 1,
            collapsed: false,
        };

        // Remove selected components from main list
        const remainingComponents = components.filter(c => !c.selected);

        setSubProducts([...subProducts, newSubProduct]);
        setComponents(remainingComponents);
    };

    // Ungroup a sub-product back to individual components at their original positions
    const handleUngroupSubProduct = (subProductId) => {
        const subProduct = subProducts.find(sp => sp.id === subProductId);
        if (!subProduct) return;

        // Restore components at their original indices when possible
        const restored = [...components];
        const toInsert = subProduct.components
            .map(c => ({ ...c, selected: false }))
            .sort((a, b) => (a._originalIndex ?? Infinity) - (b._originalIndex ?? Infinity));

        for (const comp of toInsert) {
            const idx = comp._originalIndex;
            const { _originalIndex, ...clean } = comp;
            if (idx != null && idx <= restored.length) {
                restored.splice(idx, 0, clean);
            } else {
                restored.push(clean);
            }
        }

        setComponents(restored);
        setSubProducts(subProducts.filter(sp => sp.id !== subProductId));
    };

    // Remove a sub-product (and its components if any)
    const handleRemoveSubProduct = (subProductId) => {
        setSubProducts(subProducts.filter(sp => sp.id !== subProductId));
    };

    // Toggle sub-product collapsed state
    const handleToggleSubProductCollapse = (subProductId) => {
        setSubProducts(subProducts.map(sp =>
            sp.id === subProductId ? { ...sp, collapsed: !sp.collapsed } : sp
        ));
    };

    // Update sub-product properties
    const handleUpdateSubProduct = (subProductId, updates) => {
        setSubProducts(subProducts.map(sp =>
            sp.id === subProductId ? { ...sp, ...updates } : sp
        ));
    };

    // Update a component inside a sub-product and recalculate subtotal
    const handleUpdateComponentInSubProduct = (subProductId, componentId, updates) => {
        setSubProducts(subProducts.map(sp => {
            if (sp.id !== subProductId) return sp;
            const updatedComponents = sp.components.map(c =>
                c.id === componentId ? { ...c, ...updates } : c
            );
            const updatedSubtotal = updatedComponents.reduce(
                (sum, c) => sum + ((parseFloat(c.price) || 0) * (c.amount || 1)), 0
            );
            return { ...sp, components: updatedComponents, subtotal: updatedSubtotal };
        }));
    };

    // Remove component from inside a sub-product
    const handleRemoveFromSubProduct = (subProductId, componentId) => {
        const subProduct = subProducts.find(sp => sp.id === subProductId);
        if (!subProduct) return;

        const updatedComponents = subProduct.components.filter(c => c.id !== componentId);

        if (updatedComponents.length === 0) {
            // Auto-delete sub-product if it becomes empty
            setSubProducts(subProducts.filter(sp => sp.id !== subProductId));
        } else {
            const updatedSubtotal = updatedComponents.reduce(
                (sum, c) => sum + ((parseFloat(c.price) || 0) * (c.amount || 1)), 0
            );
            setSubProducts(subProducts.map(sp =>
                sp.id === subProductId
                    ? { ...sp, components: updatedComponents, subtotal: updatedSubtotal }
                    : sp
            ));
        }
    };

    // Live total recalculation when components, sub-products, or product quantity change
    const computedTotal = useMemo(() => {
        const componentTotal = components.reduce(
            (sum, c) => sum + ((parseFloat(c.price) || 0) * (c.amount || 1)), 0
        );
        const subProductTotal = subProducts.reduce(
            (sum, sp) => sum + ((parseFloat(sp.subtotal) || 0) * (sp.amount || 1)), 0
        );
        const subtotal = componentTotal + subProductTotal;
        return subtotal * (totalAmount || 1);
    }, [components, subProducts, totalAmount]);

    useEffect(() => {
        setTotalPrice(computedTotal);
    }, [computedTotal]);

    // Reorder components via drag-and-drop
    const handleReorderComponent = (fromIndex, toIndex) => {
        setComponents(prev => {
            const updated = [...prev];
            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, moved);
            return updated;
        });
    };

    // Drop handler for adding components via DnD
    const handleDropItem = async (componentData, subProductId = null) => {
        // CASE 1: Dropping a "Product" -> Creates a new Sub-Product group
        if (componentData.type === 'product') {
            const newId = Date.now();
            const newSubProduct = {
                id: newId,
                name: componentData.name,
                type: 'product',
                components: [],
                subtotal: componentData.price || 0,
                currency: componentData.currency || selectedCurrency,
                unit: componentData.unit || 'unit',
                amount: 1,
                collapsed: false,
                locked: true,
                productRefId: componentData.id,
            };
            setSubProducts(prev => [...prev, newSubProduct]);

            // Fetch actual components from backend
            const fetchedComponents = await fetchProductComponents(componentData.id);
            setSubProducts(prev =>
                prev.map(sp =>
                    sp.id === newId
                        ? { ...sp, components: fetchedComponents }
                        : sp
                )
            );
            return;
        }

        // CASE 2: Dropping a "Material" into a specific Sub-Product
        if (subProductId) {
            setSubProducts(subProducts.map(sp => {
                if (sp.id === subProductId) {
                    const newComponent = {
                        id: Date.now(),
                        ...componentData,
                        amount: 1,
                        currency: selectedCurrency,
                        unit: 'kg',
                        source: 'Supplier A',
                        destination: 'Factory B',
                        selected: false,
                    };
                    return { ...sp, components: [...sp.components, newComponent] };
                }
                return sp;
            }));
        } else {
            // CASE 3: Dropping a "Material" into the main list
            handleAddComponent(componentData);
        }
    };

    // Handle save
    const handleSave = async () => {
        const modelData = {
            id: modelId || `model_${Date.now()}`,
            name: modelName || 'New Model',
            description: modelDescription,
            components,
            subProducts,
            currency: selectedCurrency,
            unit: selectedUnit,
            totalPrice,
            totalAmount,
            imageFile, // File object for upload after product creation
        };

        if (onSave) {
            await onSave(modelData);
            return;
        }

        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="model-builder-overlay" ref={overlayRef}>
            {/* Backdrop */}
            <div className="model-builder-backdrop" onClick={onClose} />

            {/* Content */}
            <div className="model-builder-container">
                {/* Left Panel - Component Search */}
                <ComponentSearchPanel onAddComponent={handleAddComponent} />

                {/* Right Panel - Model Builder */}
                <div className="model-builder-main">

                    {/* Product Top Section */}
                    <div className="model-builder-product-row">
                        <div className="model-builder-header">
                            <h2 className="model-builder-title">MODEL BUILDER</h2>
                        </div>

                        {/* Top Row: Image | Name | Controls */}
                        <div className="product-top-row">
                            <div className="product-image-container">
                                <input
                                    type="file"
                                    ref={imageInputRef}
                                    accept="image/*"
                                    className="u-hidden"
                                    onChange={handleImageSelect}
                                />
                                <div
                                    className="product-image-placeholder product-image-placeholder--clickable"
                                    onClick={() => imageInputRef.current?.click()}
                                    title="Click to upload image"
                                >
                                    {imagePreview ? (
                                        <img
                                            src={imagePreview}
                                            alt="Product"
                                            className="product-image-preview"
                                        />
                                    ) : (
                                        <div className="product-image-upload-hint">
                                            <Upload size={24} color="rgba(255,255,255,0.3)" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-group form-group--flex">
                                <label className="form-label">Product Name</label>
                                <input
                                    type="text"
                                    className="glass-pill-input"
                                    placeholder="Battery Pack Case"
                                    value={modelName}
                                    onChange={(e) => setModelName(e.target.value)}
                                />
                            </div>

                            <div className="product-controls-grid">
                                <div className="form-group form-group--w-90">
                                    <label className="form-label">Price</label>
                                    <input
                                        type="text"
                                        className="glass-pill-input"
                                        value={formatPrice(totalPrice)}
                                        readOnly
                                        title="Auto-calculated from components"
                                    />
                                </div>
                                <div className="form-group form-group--w-70">
                                    <label className="form-label">Amount</label>
                                    <input
                                        type="number"
                                        className="glass-pill-input"
                                        value={totalAmount}
                                        onChange={(e) => setTotalAmount(parseInt(e.target.value) || 1)}
                                    />
                                </div>
                                <div className="form-group form-group--w-120">
                                    <label className="form-label">Currency</label>
                                    <div className="currency-pill-wrapper">
                                        <div className="currency-icon-dot">$</div>
                                        <select
                                            className="glass-pill-select"
                                            value={selectedCurrency}
                                            onChange={(e) => setSelectedCurrency(e.target.value)}
                                        >
                                            <option value="USD">USD</option>
                                            <option value="EUR">EUR</option>
                                            <option value="CNY">CNY</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-group form-group--w-90">
                                    <label className="form-label">Unit</label>
                                    <select
                                        className="glass-pill-select"
                                        value={selectedUnit}
                                        onChange={(e) => setSelectedUnit(e.target.value)}
                                    >
                                        <option value="Case">Case</option>
                                        <option value="kg">kg</option>
                                        <option value="mt">mt</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Bottom Row: Description */}
                        <div className="product-bottom-row">
                            <div className="form-group full-width">
                                <label className="form-label">Model Description</label>
                                <input
                                    type="text"
                                    className="glass-pill-input"
                                    placeholder="Battery Pack Case for Mercedes EQS Model Series..."
                                    value={modelDescription}
                                    onChange={(e) => setModelDescription(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Components Table with Sub-Products */}
                    <div className="model-builder-table-container">
                        <ModelBuilderTable
                            components={components}
                            subProducts={subProducts}
                            onRemove={handleRemoveComponent}
                            onUpdate={handleUpdateComponent}
                            onUpdateInSubProduct={handleUpdateComponentInSubProduct}
                            onReorder={handleReorderComponent}
                            onUngroupSubProduct={handleUngroupSubProduct}
                            onRemoveSubProduct={handleRemoveSubProduct}
                            onToggleSubProductCollapse={handleToggleSubProductCollapse}
                            onUpdateSubProduct={handleUpdateSubProduct}
                            onRemoveFromSubProduct={handleRemoveFromSubProduct}
                            onDropItem={handleDropItem}
                            onGroupSelected={handleGroupSelected}
                            selectedCount={selectedCount}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="model-builder-actions">
                        <button className="btn-glass-cancel" onClick={onClose}>
                            CANCEL
                        </button>
                        <button className="btn-glass-save" onClick={handleSave}>
                            SAVE
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModelBuilderOverlay;
